import { HttpContext } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, of, tap, timeout } from 'rxjs';

import { API_ROUTES } from '../../../../consts/api-routes.const';
import { SKIP_SERVER_ERROR_RETRY } from '../../../../interceptors/api-error.interceptor';
import { BookingStateService, HttpService, LicensePlateService } from '../../../../services';
import { BookingDataService } from '../../../../services/booking-data.service';
import { FormStateService } from '../../../../services/form-state.service';
import { MaintenanceStatus } from '../../plan-appointment/license-plate-selection/interfaces/maintenance-status.interface';
import { UserService } from '../../plan-appointment/license-plate-selection/services/user.service';

const LOGOUT_TIMEOUT_MS = 8000;
const AUTHENTICATED_SESSION_STORAGE_KEY = 'authenticated';
const AUTHENTICATED_SESSION_STORAGE_KEY_VALUE = 'true';
const AUTHENTICATED_SESSION_EMAIL_KEY = 'email';
const GUEST_SESSION_STORAGE_KEY = 'guest';
const GUEST_SESSION_STORAGE_KEY_VALUE = 'true';
const SYNTEC_SESSION_COOKIE_KEY = 'syntec_session';
const WPP_DATA_KEY = 'userData';

interface UserData {
  mileage?: MaintenanceStatus;
  mileageUserEntered?: boolean;
  // Kenteken (genormaliseerd) waar de mileage bij hoort — een andere auto mag 'm nooit zien.
  mileageLicensePlate?: string;
}

@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly http: HttpService = inject(HttpService);
  private readonly licensePlateService: LicensePlateService = inject(LicensePlateService);
  private readonly bookingData: BookingDataService = inject(BookingDataService);
  private readonly formState: FormStateService = inject(FormStateService);
  private readonly bookingState: BookingStateService = inject(BookingStateService);
  private readonly userService: UserService = inject(UserService);
  public readonly isAuthenticated = signal<boolean>(
    sessionStorage.getItem(AUTHENTICATED_SESSION_STORAGE_KEY) ===
      AUTHENTICATED_SESSION_STORAGE_KEY_VALUE || false,
  );
  // Guest = "verder zonder inloggen": e-mail is bewaard maar er is geen SMS-validatie
  // gedaan, dus géén echte backend-sessie. Bewust losgekoppeld van isAuthenticated zodat
  // de bestaande klantidentificatie ongemoeid blijft; de planner-guards laten een gast
  // door op isAuthenticated() || isGuest().
  public readonly isGuest = signal<boolean>(
    sessionStorage.getItem(GUEST_SESSION_STORAGE_KEY) === GUEST_SESSION_STORAGE_KEY_VALUE,
  );
  public readonly mileage = signal<MaintenanceStatus | null>(this.readMileageFromStorage());
  public readonly mileageUserEntered = signal<boolean>(this.readMileageUserEnteredFromStorage());

  // Probes whether the e-mail still has a valid WPP (planner) session by hitting
  // the user-profile endpoint. WPP authenticates against the planner firewall,
  // not the core one, so /core/auth/session is the wrong signal — the profile
  // route requires ROLE_API_USER and answers 200 with a session, 401/403
  // without. Any error resolves to false. SKIP_AUTH_REDIRECT keeps the global
  // auth-interceptor out of it so the probe's 401 doesn't clear+redirect (and
  // strip the URL's query params) — the caller (StartPage) handles it.
  public checkSession(email: string): Observable<boolean> {
    return this.http.get<unknown>(API_ROUTES.USER.profile(email)).pipe(
      map((): boolean => true),
      catchError((): Observable<boolean> => of(false)),
    );
  }

  public logout(): Observable<void> {
    // Guests and signed-in customers have a planner session to end; anyone else is wiped locally.
    if (!this.isAuthenticated() && !this.isGuest()) {
      this.clearSession();

      return of(undefined);
    }

    const context: HttpContext = new HttpContext().set(SKIP_SERVER_ERROR_RETRY, true);
    return this.http.get<void>(API_ROUTES.PLANNER.logout(), context).pipe(
      timeout(LOGOUT_TIMEOUT_MS),
      map((): void => undefined),
      catchError((): Observable<void> => of(undefined)),
      tap((): void => this.clearSession()),
    );
  }

  public setAuthenticated(email: string): void {
    sessionStorage.setItem(
      AUTHENTICATED_SESSION_STORAGE_KEY,
      AUTHENTICATED_SESSION_STORAGE_KEY_VALUE,
    );
    sessionStorage.setItem(AUTHENTICATED_SESSION_EMAIL_KEY, email);
    this.userService.email.set(email);
    this.isAuthenticated.set(true);
  }

  // "Verder zonder inloggen": bewaar het e-mailadres in de sessie en markeer als gast.
  // Bewust NIET setAuthenticated — een gast heeft geen geverifieerde backend-sessie.
  public setGuest(email: string): void {
    sessionStorage.setItem(GUEST_SESSION_STORAGE_KEY, GUEST_SESSION_STORAGE_KEY_VALUE);
    sessionStorage.setItem(AUTHENTICATED_SESSION_EMAIL_KEY, email);
    this.userService.email.set(email);
    this.isGuest.set(true);
  }

  public setUserData(data: UserData): void {
    const merged: UserData = { ...this.getUserData(), ...data };
    sessionStorage.setItem(WPP_DATA_KEY, JSON.stringify(merged));
    if (data.mileage !== undefined) {
      this.mileage.set(data.mileage);
    }
    if (data.mileageUserEntered !== undefined) {
      this.mileageUserEntered.set(data.mileageUserEntered);
    }
  }

  public getUserData(): UserData {
    const userData: string | null = sessionStorage.getItem(WPP_DATA_KEY);
    if (!userData) {
      return {};
    }
    try {
      return JSON.parse(userData) as UserData;
    } catch {
      return {};
    }
  }

  // De sessie-mileage is kenteken-gebonden: alleen teruggeven voor de auto waar 'ie bij
  // hoort. Backend-state en OCW zijn leidend; dit is enkel de in-flow invoer/bevestiging.
  public mileageKmForPlate(licensePlate: string | null | undefined): number | null {
    const data: UserData = this.getUserData();
    if (!licensePlate || data.mileage?.mileageKm == null || !data.mileageLicensePlate) {
      return null;
    }
    const normalized: string = this.licensePlateService.normalizeLicensePlate(licensePlate);
    return data.mileageLicensePlate === normalized ? data.mileage.mileageKm : null;
  }

  // Per-boeking scratch-data: weg zodra een boeking is afgerond of een nieuwe journey start.
  // BookingDataService blijft bewust staan — de confirmation-pagina toont daar nog uit.
  public clearBookingData(): void {
    const data: UserData = this.getUserData();
    delete data.mileage;
    delete data.mileageUserEntered;
    delete data.mileageLicensePlate;
    sessionStorage.setItem(WPP_DATA_KEY, JSON.stringify(data));
    this.mileage.set(null);
    this.mileageUserEntered.set(false);
    this.formState.clearAll();
    this.bookingState.clear();
  }

  private readMileageFromStorage(): MaintenanceStatus | null {
    return this.getUserData().mileage ?? null;
  }

  private readMileageUserEnteredFromStorage(): boolean {
    return this.getUserData().mileageUserEntered === true;
  }

  public getAuthenticatedEmail(): string | null {
    return sessionStorage.getItem(AUTHENTICATED_SESSION_EMAIL_KEY) || null;
  }

  public clearSession(): void {
    // Booking scratch first (mileage + form caches + booking-state signal) …
    this.clearBookingData();

    // … then the session identity + the remaining per-session data.
    sessionStorage.removeItem(AUTHENTICATED_SESSION_STORAGE_KEY);
    sessionStorage.removeItem(GUEST_SESSION_STORAGE_KEY);
    sessionStorage.removeItem(AUTHENTICATED_SESSION_EMAIL_KEY);
    sessionStorage.removeItem(WPP_DATA_KEY);

    // clear user search and vehicle data
    this.licensePlateService.clear();

    // wipe booking selections (contains user PII on step 4)
    this.bookingData.clear();

    this.deleteCookie(SYNTEC_SESSION_COOKIE_KEY);
    this.userService.email.set(null);
    this.userService.clear();
    this.isAuthenticated.set(false);
    this.isGuest.set(false);
  }

  private deleteCookie(name: string): void {
    document.cookie = `${name}=; Max-Age=0; path=/`;
  }
}
